// The column of page thumbnails beside the pages (desktop only): each drawn as
// a small image once it scrolls near, the page shown outlined and kept in view,
// a click goes to the page.
import React, { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { isCancelled } from "./pdf"

const THUMB_WIDTH = 96

function Thumb({ pdf, number, size, current, onSelect, root, label }) {
  const ref = useRef(null)
  const [near, setNear] = useState(false)
  const [src, setSrc] = useState(null)
  const height = Math.round((THUMB_WIDTH * size.height) / size.width)

  useEffect(() => {
    const el = ref.current
    if (!el || near) return
    const observer = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && setNear(true), { root: root.current, rootMargin: "300px 0px" })
    observer.observe(el)
    return () => observer.disconnect()
  }, [near, root])

  // drawn once, as a JPEG the browser keeps compressed
  useEffect(() => {
    if (!near) return
    let url = null
    let task = null
    let gone = false
    ;(async () => {
      const page = await pdf.getPage(number)
      if (gone) return
      const ratio = Math.min(2, window.devicePixelRatio || 1)
      const viewport = page.getViewport({ scale: (THUMB_WIDTH * ratio) / size.width })
      const canvas = document.createElement("canvas")
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)
      const context = canvas.getContext("2d", { alpha: false })
      context.fillStyle = "#fff"
      context.fillRect(0, 0, canvas.width, canvas.height)
      task = page.render({ canvasContext: context, canvas, viewport })
      await task.promise
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.8))
      canvas.width = 0
      if (gone || !blob) return
      url = URL.createObjectURL(blob)
      setSrc(url)
    })().catch((error) => !isCancelled(error) && console.warn("PDFViewer thumbnail", error))
    return () => {
      gone = true
      task?.cancel()
      if (url) URL.revokeObjectURL(url)
    }
  }, [near, pdf, number, size.width])

  // the page shown kept in sight in the column (the column alone scrolled, not the page around it)
  useEffect(() => {
    const el = ref.current
    const box = root.current
    if (!current || !el || !box) return
    const top = el.offsetTop - 12
    const bottom = el.offsetTop + el.offsetHeight + 12
    if (top < box.scrollTop) box.scrollTop = top
    else if (bottom > box.scrollTop + box.clientHeight) box.scrollTop = bottom - box.clientHeight
  }, [current, root])

  return (
    <button type="button" ref={ref} onClick={() => onSelect(number)} className="flex flex-col items-center gap-1 shrink-0" aria-label={label} aria-current={current ? "page" : undefined}>
      <span className={twMerge("block bg-white shadow-sm ring-1 ring-admin_bordered overflow-hidden transition-shadow", current && "ring-2 ring-admin_primary")} style={{ width: THUMB_WIDTH, height }}>
        {src && <img src={src} alt="" className="w-full h-full" draggable={false} />}
      </span>
      <span className={twMerge("text-xs tabular-nums text-admin_text_secondary", current && "text-admin_primary font-semibold")}>{number}</span>
    </button>
  )
}

export default function MiniMap({ pdf, sizes, current, onSelect, texts }) {
  const root = useRef(null)
  return (
    <div
      ref={root}
      className="relative hidden md:flex flex-col items-center gap-3 w-[132px] shrink-0 overflow-y-auto py-4 border-r border-admin_bordered bg-admin_neutral_2"
      aria-label={texts.pages}
      data-testid="pdf-minimap"
    >
      {sizes.map((size, i) => (
        <Thumb key={i} pdf={pdf} number={i + 1} size={size} current={current === i + 1} onSelect={onSelect} root={root} label={texts.pageN.replace("%s", i + 1)} />
      ))}
    </div>
  )
}
